import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Paper,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Favorite as HeartIcon,
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import landingTheme from '../../theme/landingTheme';
import LandingLayout from '../../components/LandingLayout';

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: <HeartIcon sx={{ fontSize: 50 }} />,
      title: 'Innovazione',
      description: 'Costante ricerca di nuove soluzioni tecnologiche per costruire il futuro'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 50 }} />,
      title: 'Qualità del Servizio',
      description: 'Team professionale specializzato in nuove tecnologie e soluzioni personalizzate'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 50 }} />,
      title: 'Aggiornamento Continuo',
      description: 'Formazione senza limiti su firmware, software e hardware'
    },
    {
      icon: <SupportIcon sx={{ fontSize: 50 }} />,
      title: 'Soluzioni su Misura',
      description: 'Trasformiamo le idee dei clienti in prodotti professionali completi'
    }
  ];

  const team = [
    {
      name: 'Rita Alessandri',
      role: 'CEO',
      bio: 'Designer e Web Design specialist - Progettazione grafica web application e 3D',
      email: 'rita@reaktice.com',
      avatar: 'RA'
    },
    {
      name: 'Marco Melone',
      role: 'Project Manager',
      bio: 'Electronic Designer - Progettazione firmware, PCB, prototipi e IoT',
      email: 'marco@reaktice.com',
      avatar: 'MM'
    },
    {
      name: 'Lorenzo Melone',
      role: 'Co-founder',
      bio: 'Software Developer - Servizi per il Metaverso e Intelligenza Artificiale',
      email: 'lorenzo@reaktice.com',
      avatar: 'LM'
    },
    {
      name: 'Tommaso Bianchi',
      role: 'Co-founder',
      bio: 'Software Developer - Sviluppo software e soluzioni innovative',
      avatar: 'TB'
    }
  ];

  const milestones = [
    { year: '1999', title: 'Le Radici', description: 'Nasce l\'esperienza da cui Reaktice affonda le sue radici' },
    { year: '2019', title: 'Fondazione Reaktice', description: 'Nasce Reaktice a Firenze con nuovi talenti dinamici' },
    { year: '2020', title: 'Espansione Team', description: 'Il team si amplia con specialisti in informatica, design e web design' },
    { year: '2021', title: 'IoT Solutions', description: 'Lancio delle prime soluzioni IoT e sensoristica avanzata' },
    { year: '2023', title: 'AI & Metaverso', description: 'Integrazione di Intelligenza Artificiale e servizi per il Metaverso' },
    { year: '2025', title: 'Innovazione Continua', description: 'Oltre 200 progetti realizzati tra IoT, software e scanner 3D' }
  ];

  return (
    <LandingLayout>
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, landingTheme.colors.primary 0%, landingTheme.colors.primaryLight 100%)' }}>
        {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 6, pb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip 
            label="LA NOSTRA STORIA" 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white', 
              fontWeight: 700,
              mb: 3,
              px: 2,
              py: 0.5,
              border: '1px solid rgba(255,255,255,0.3)'
            }} 
          />
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              mb: 3
            }}
          >
            Reaktice - Innovazione Tecnologica
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontWeight: 400,
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.7,
              mb: 2
            }}
          >
            Reaktice è un'innovativa azienda fiorentina, che affonda le sue radici nell'esperienza 
            di una precedente azienda nata nel 1999, e si arricchisce con l'energia di nuovi talenti 
            dinamici specializzati in informatica, design e web design.
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255,255,255,0.95)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontWeight: 600,
              fontStyle: 'italic',
              maxWidth: '800px',
              mx: 'auto',
              mt: 3
            }}
          >
            "ANALIZZARE IL PROBLEMA, COMPRENDERE I REQUISITI, IMMAGINARE LA SOLUZIONE"
          </Typography>
        </Box>

        {/* Mission Statement */}
        <Card sx={{ 
          p: 6, 
          mb: 8,
          bgcolor: 'rgba(255,255,255,0.15)', 
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                p: 4,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.3)'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                  🎯 La Nostra Missione
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, fontSize: '1.1rem', fontWeight: 500 }}>
                  Digitalizzare e semplificare la gestione delle strutture per anziani, 
                  permettendo agli operatori di dedicare più tempo alla cura e meno alla burocrazia.
                  Vogliamo che ogni residente riceva l'attenzione che merita.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                p: 4,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.3)'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                  🔭 La Nostra Visione
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, fontSize: '1.1rem', fontWeight: 500 }}>
                  Diventare il partner tecnologico di riferimento per tutte le strutture 
                  assistenziali in Italia, portando innovazione, intelligenza artificiale 
                  e IoT al servizio dell'assistenza geriatrica.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Values */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            I Nostri Valori
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.9)', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            I principi che guidano il nostro lavoro ogni giorno
          </Typography>
          
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ 
                  height: '100%',
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)'
                  }
                }}>
                  <Box sx={{ 
                    color: landingTheme.colors.primary,
                    bgcolor: 'rgba(5, 134, 178, 0.1)',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, fontWeight: 500 }}>
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Timeline */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Il Nostro Percorso
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.9)', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            Dalle origini ad oggi
          </Typography>

          <Grid container spacing={3}>
            {milestones.map((milestone, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ 
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}>
                  <Chip 
                    label={milestone.year}
                    sx={{ 
                      position: 'absolute',
                      top: -15,
                      left: 20,
                      bgcolor: landingTheme.colors.primary,
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1rem',
                      height: 32,
                      boxShadow: 2
                    }}
                  />
                  <CardContent sx={{ pt: 4, pb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, fontWeight: 500 }}>
                      {milestone.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Team */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Il Nostro Team
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.9)', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            Le persone dietro il prodotto
          </Typography>

          <Grid container spacing={4}>
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Avatar 
                      sx={{ 
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: landingTheme.colors.primary,
                        fontSize: '2rem',
                        fontWeight: 700,
                        boxShadow: 3
                      }}
                    >
                      {member.avatar}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      {member.name}
                    </Typography>
                    <Chip 
                      label={member.role} 
                      size="small"
                      sx={{ 
                        mb: 2,
                        bgcolor: 'rgba(5, 134, 178, 0.1)',
                        color: landingTheme.colors.primary,
                        fontWeight: 600
                      }}
                    />
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, fontWeight: 500 }}>
                      {member.bio}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Stats */}
        <Card sx={{ 
          p: 6, 
          mb: 8,
          bgcolor: 'rgba(255,255,255,0.15)', 
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 6, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            I Numeri del Nostro Successo
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 1, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                  200+
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                  Strutture Attive
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 1, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                  5.000+
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                  Residenti Gestiti
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 1, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                  98%
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                  Soddisfazione Clienti
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 1, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                  24/7
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                  Supporto Disponibile
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* CTA */}
        <Card sx={{ 
          p: 6, 
          bgcolor: 'rgba(255,255,255,0.95)', 
          backdropFilter: 'blur(10px)',
          textAlign: 'center'
        }}>
          <GroupsIcon sx={{ fontSize: 80, color: landingTheme.colors.primary, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 2 }}>
            Vuoi Unirti alla Nostra Famiglia?
          </Typography>
          <Typography variant="h6" sx={{ color: '#666', mb: 4 }}>
            Inizia oggi la trasformazione digitale della tua struttura
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: landingTheme.colors.primary,
                fontWeight: 700,
                px: 4,
                py: 1.5
              }}
            >
              Prova Gratuita 30 Giorni
            </Button>
            <Button 
             
              size="large"
              onClick={() => navigate('/demo')}
              sx={{ 
                borderColor: landingTheme.colors.primary,
                borderWidth: 2,
                color: landingTheme.colors.primary,
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderWidth: 2
                }
              }}
            >
              Richiedi una Demo
            </Button>
          </Stack>
        </Card>
      </Container>
      </Box>
    </LandingLayout>
  );
};

export default AboutPage;
